import { iConversation } from "@sdk/conversations/conversations.models";
import {
  iContact,
  iContactConnection,
  iContactConnectionData,
} from "@sdk/crm/crm.models";
import {
  getGroupContactDisplayLabel,
  getGroupContactSubtitle,
  isGroupContact,
  isGroupConversation,
} from "./is-wa-cloud-native-group";

const WA_CLOUD_NATIVE = "WHATS_APP_CLOUD_NATIVE";
const FALLBACK_LABEL = "WhatsApp User";
const PLACEHOLDER_NAMES = new Set(["n/a", "na", "unknown", "-"]);

/** BSUID format: `{CC}.{alphanumeric}` or `{CC}.ENT.{alphanumeric}` */
const BSUID_PATTERN = /^[A-Z]{2}\.(?:ENT\.)?[A-Za-z0-9]+$/;

type ConversationLike = Pick<
  iConversation,
  "connectionId" | "connectionType" | "data" | "contact"
>;
type ContactLike = Pick<
  iContact,
  "data" | "metaData" | "isGroupContact" | "contactType"
>;

const isUsableNamePart = (value?: string | null) => {
  if (!value) {
    return false;
  }
  const trimmed = value.trim();
  if (!trimmed) {
    return false;
  }
  return !PLACEHOLDER_NAMES.has(trimmed.toLowerCase());
};

export const formatWAUsername = (username: string) => {
  const trimmed = username.trim().replace(/^@+/, "");
  if (!trimmed) {
    return "";
  }
  return `@${trimmed}`;
};

export const isBsuid = (value?: string | null): boolean => {
  if (!value?.trim()) {
    return false;
  }
  return BSUID_PATTERN.test(value.trim());
};

export const getWACloudNativeConnection = (
  contact?: ContactLike | null,
  connectionId?: string,
): iContactConnection | undefined => {
  const connections = contact?.metaData?.connections;
  if (!connections?.length) {
    return undefined;
  }

  if (connectionId) {
    const matched = connections.find(
      (connection) =>
        connection.type === WA_CLOUD_NATIVE &&
        connection.connectionId === connectionId,
    );
    if (matched) {
      return matched;
    }
  }

  return connections.find((connection) => connection.type === WA_CLOUD_NATIVE);
};

export const getWACloudNativeConnectionData = (
  contact?: ContactLike | null,
  connectionId?: string,
): iContactConnectionData | undefined => {
  return getWACloudNativeConnection(contact, connectionId)?.data;
};

const getContactDisplayName = (contact?: ContactLike | null) => {
  const firstName = contact?.data?.firstName;
  const lastName = contact?.data?.lastName;
  const parts = [firstName, lastName].filter(isUsableNamePart);
  return parts.join(" ").trim();
};

const resolveUsername = (
  conversation?: ConversationLike | null,
  contact?: ContactLike | null,
) => {
  const fromConversation = conversation?.data?.waUsername as string | undefined;
  if (fromConversation?.trim()) {
    return fromConversation.trim();
  }

  const connectionData = getWACloudNativeConnectionData(
    contact,
    conversation?.connectionId,
  );
  return connectionData?.waUsername?.trim() || undefined;
};

const resolvePhone = (
  conversation?: ConversationLike | null,
  contact?: ContactLike | null,
) => {
  const primaryMobile = contact?.data?.primaryMobile?.trim();
  if (primaryMobile) {
    return primaryMobile;
  }

  const fromConversation = conversation?.data?.waPhoneId as string | undefined;
  if (fromConversation?.trim()) {
    return fromConversation.trim();
  }

  const connectionData = getWACloudNativeConnectionData(
    contact,
    conversation?.connectionId,
  );
  return connectionData?.waPhoneId?.trim() || undefined;
};

/**
 * Canonical agent-facing label for WHATS_APP_CLOUD_NATIVE.
 * Groups: group subject path (never person phone/username fallbacks).
 * People: contact name → @username → phone → "WhatsApp User".
 * Never returns raw BSUID.
 */
export const getWACloudNativeDisplayLabel = ({
  conversation,
  contact,
}: {
  conversation?: ConversationLike | null;
  contact?: ContactLike | null;
}): string => {
  if (isGroupConversation(conversation, contact)) {
    return getGroupContactDisplayLabel({ conversation, contact });
  }

  const name = getContactDisplayName(contact);
  if (name) {
    return name;
  }

  const username = resolveUsername(conversation, contact);
  if (username) {
    return formatWAUsername(username);
  }

  const phone = resolvePhone(conversation, contact);
  if (phone) {
    return phone;
  }

  return FALLBACK_LABEL;
};

/**
 * Subtitle / channel metadata line.
 * Groups: participant count / suspended · WhatsApp Group
 * People:
 * - username: `@user · WhatsApp`
 * - phone only: phone · WhatsApp
 * - neither: `WhatsApp`
 */
export const getWACloudNativeSubtitle = ({
  conversation,
  contact,
}: {
  conversation?: ConversationLike | null;
  contact?: ContactLike | null;
}): string => {
  if (isGroupConversation(conversation, contact)) {
    return getGroupContactSubtitle({ conversation, contact });
  }

  const username = resolveUsername(conversation, contact);
  if (username) {
    return `${formatWAUsername(username)} · WhatsApp`;
  }

  const phone = resolvePhone(conversation, contact);
  if (phone) {
    return `${phone} · WhatsApp`;
  }

  return "WhatsApp";
};

/**
 * Whether the contact can be messaged / called on Cloud Native after backend BSUID outbound.
 * Group contacts are not person outbound targets.
 */
export const hasWACloudNativeOutboundIdentity = (
  contact?: ContactLike | null,
  connectionId?: string,
): boolean => {
  if (isGroupContact(contact)) {
    return false;
  }

  if (contact?.data?.primaryMobile?.trim()) {
    return true;
  }

  const connectionData = getWACloudNativeConnectionData(contact, connectionId);
  if (connectionData?.waPhoneId?.trim()) {
    return true;
  }
  if (connectionData?.waUserId?.trim()) {
    return true;
  }

  return false;
};
